/**
 * Config Presets Store (Zustand) — Web
 *
 * Every config IS a preset. There's always a "Default" preset, plus user-created ones.
 * The active preset = the config being edited. Toggling factors edits the active preset
 * in-place. Switching presets changes which config is active.
 *
 * Synced via PUT /api/auth/config-presets (custom presets only -- Default is derived).
 */

import { authAwareFetch } from '@contexts/authHttpUtils';
import type { AnalysisConfig as SharedAnalysisConfig } from '@parlay-picker/shared/analysisConfig';
import type { ConfigPreset } from '@parlay-picker/shared/configPreset';
import { DEFAULT_PRESET_ID } from '@parlay-picker/shared/configPreset';
import {
  createPresetsSlice,
  getCustomPresets,
  isSwitchingPreset,
  makeDefaultPreset,
  type PresetsState,
  syncPresetsToServer,
} from '@parlay-picker/shared/stores/presetsStoreCore';
import type { AnalysisConfig } from '@views/picks/constants';
import { DEFAULT_ANALYSIS_CONFIG } from '@views/picks/constants';
import { deepMergeConfig } from '@views/picks/utils';
import { create } from 'zustand';
import { useAnalysisConfigStore } from './analysisConfigStore';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const STORAGE_KEY = 'configPresets';
const ACTIVE_PRESET_KEY = 'activePresetId';

// ---------------------------------------------------------------------------
// Web-specific localStorage helpers
// ---------------------------------------------------------------------------

function persistToLocalStorage(presets: ConfigPreset[], activePresetId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    localStorage.setItem(ACTIVE_PRESET_KEY, activePresetId);
  } catch {
    // localStorage may be full or unavailable
  }
}

function loadPresetsFromLocalStorage(): ConfigPreset[] {
  if (typeof window === 'undefined') return [makeDefaultPreset()];
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as ConfigPreset[];
      // Ensure Default preset always exists
      if (!parsed.some((p) => p.id === DEFAULT_PRESET_ID)) {
        parsed.unshift(makeDefaultPreset());
      }
      return parsed;
    }
  } catch {
    // fall through
  }
  return [makeDefaultPreset()];
}

function loadActivePresetIdFromLocalStorage(): string {
  if (typeof window === 'undefined') return DEFAULT_PRESET_ID;
  try {
    return localStorage.getItem(ACTIVE_PRESET_KEY) ?? DEFAULT_PRESET_ID;
  } catch {
    return DEFAULT_PRESET_ID;
  }
}

// ---------------------------------------------------------------------------
// Web-specific extended state (adds loadFromLocalStorage + syncToServer)
// ---------------------------------------------------------------------------

interface WebPresetsState extends PresetsState {
  /** Load from localStorage (non-logged-in or initial) */
  loadFromLocalStorage: () => void;
  /** Sync custom presets to server */
  syncToServer: (authFetch: AuthFetch) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Store creation
// ---------------------------------------------------------------------------

const initialPresets = loadPresetsFromLocalStorage();
const initialActiveId = loadActivePresetIdFromLocalStorage();

export const usePresetsStore = create<WebPresetsState>((set, get, store) => {
  // Build the core slice from the factory
  const coreSlice = createPresetsSlice(
    {
      apiFetch: authAwareFetch,
      getConfig: () => useAnalysisConfigStore.getState().config as SharedAnalysisConfig,
      setConfig: (config) => useAnalysisConfigStore.getState().setConfig(config as AnalysisConfig),
      mergeConfig: (_, override) =>
        deepMergeConfig(DEFAULT_ANALYSIS_CONFIG, override as Partial<AnalysisConfig>),
      generateId: () => crypto.randomUUID(),
      onPersist: persistToLocalStorage,
    },
    initialPresets,
    initialActiveId,
  );

  // Spread the core slice and add web-specific actions
  const coreState = coreSlice(set, get, store);

  return {
    ...coreState,

    loadFromLocalStorage: () => {
      const presets = loadPresetsFromLocalStorage();
      const activeId = loadActivePresetIdFromLocalStorage();
      const validId = presets.some((p) => p.id === activeId) ? activeId : DEFAULT_PRESET_ID;
      set({ presets, activePresetId: validId });
    },

    syncToServer: async (authFetch) => {
      await syncPresetsToServer(getCustomPresets(get().presets), authFetch);
    },
  };
});

// ---------------------------------------------------------------------------
// Config -> Preset subscription (web-specific, uses localStorage)
// ---------------------------------------------------------------------------

/**
 * Subscribe to analysisConfig changes -> update the active preset's config in-place.
 * This keeps the preset in sync when the user toggles factors.
 */
useAnalysisConfigStore.subscribe((state) => {
  if (isSwitchingPreset()) return; // Don't update during preset switch

  const { presets, activePresetId } = usePresetsStore.getState();
  const idx = presets.findIndex((p) => p.id === activePresetId);
  if (idx === -1) return;

  const configSnapshot = JSON.parse(JSON.stringify(state.config));
  // Skip if nothing changed (avoid infinite loops)
  if (JSON.stringify(presets[idx].config) === JSON.stringify(configSnapshot)) return;

  const updated = [...presets];
  updated[idx] = {
    ...updated[idx],
    config: configSnapshot,
    updatedAt: new Date().toISOString(),
  };
  usePresetsStore.setState({ presets: updated });
  persistToLocalStorage(updated, activePresetId);
});
